import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Grid,
  Card,
  CardMedia,
  CardActions,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";

import apiClient, { resolveFileUrl, useApiQuery } from "../api/client";

const AdminGallery = () => {
  const { data, isLoading, refetch } = useApiQuery("/gallery");
  const images = data || [];
  const [isCreating, setIsCreating] = useState(false);

  const [file, setFile] = useState(null);
  const [label, setLabel] = useState("");
  const [toast, setToast] = useState("");

  const handleUpload = async () => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("label", label);
    formData.append("order", images.length);
    setIsCreating(true);
    try {
      await apiClient.post("/gallery", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFile(null);
      setLabel("");
      setToast("Image added.");
      refetch();
    } catch (err) {
      setToast(err?.response?.data?.message || "Failed to upload image.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/gallery/${id}`);
      setToast("Image removed.");
      refetch();
    } catch (err) {
      setToast(err?.response?.data?.message || "Failed to remove image.");
    }
  };

  const moveOrder = async (img, direction) => {
    try {
      await apiClient.put(`/gallery/${img._id}`, buildOrderFormData(img.order + direction), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      refetch();
    } catch (err) {
      setToast(err?.response?.data?.message || "Failed to reorder image.");
    }
  };

  const buildOrderFormData = (order) => {
    const fd = new FormData();
    fd.append("order", order);
    return fd;
  };

  if (isLoading) return <Typography>Loading…</Typography>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Gallery
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Add Photo
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField label="Label" value={label} onChange={(e) => setLabel(e.target.value)} />
          <Button component="label" startIcon={<UploadRoundedIcon />} variant="outlined">
            {file ? file.name : "Choose Photo"}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={(e) => setFile(e.target.files[0])}
            />
          </Button>
          <Button variant="contained" onClick={handleUpload} disabled={!file || isCreating}>
            {isCreating ? "Uploading…" : "Upload"}
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={2}>
        {images.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              No photos in the gallery yet.
            </Typography>
          </Grid>
        )}
        {images.map((img) => (
          <Grid item xs={12} sm={6} md={4} key={img._id}>
            <Card>
              <CardMedia
                component="img"
                height="160"
                image={resolveFileUrl(img.imageUrl)}
                alt={img.label}
              />
              <CardActions sx={{ justifyContent: "space-between" }}>
                <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                  {img.label || "Untitled"}
                </Typography>
                <Box>
                  <IconButton size="small" onClick={() => moveOrder(img, -1)}>
                    ↑
                  </IconButton>
                  <IconButton size="small" onClick={() => moveOrder(img, 1)}>
                    ↓
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDelete(img._id)}>
                    <DeleteRoundedIcon />
                  </IconButton>
                </Box>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast("")}>
        <Alert severity="success" onClose={() => setToast("")}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminGallery;
